import { CardElement, Elements, useElements } from '@stripe/react-stripe-js'
import CardPaymentMethodEditorViewModel from 'billing/payment-methods/CardPaymentMethodEditorViewModel'
import {
  Form,
  FormColumn,
  FormColumns,
  FormGroupContainer,
  FormRow,
  TextInputContainer,
} from 'components/inputs'
import { CountryInput } from 'components/inputs/CountryInput'
import TextInput from 'components/inputs/TextInput'
import {
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
} from 'components/ModalSheet'
import { observer } from 'mobx-react'
import * as React from 'react'
import CardInput from './CardInput'

interface CardPaymentMethodEditorContentProps {
  vm: CardPaymentMethodEditorViewModel
}

/**
 * The CardPaymentMethodEditorContent
 */
export default observer(function CardPaymentMethodEditorContent({
  vm,
}: CardPaymentMethodEditorContentProps) {
  return (
    <>
      <ModalSheetContentBlock>
        <ModalSheetContentTitle>Add a Credit/Debit Card</ModalSheetContentTitle>
        <ModalSheetContentDescription>
          Enter your payment details below.
        </ModalSheetContentDescription>
      </ModalSheetContentBlock>
      <ModalSheetContentBlock>
        <Elements stripe={vm.stripe!} options={{ locale: 'en' }}>
          <CardPaymentMethodForm vm={vm} />
        </Elements>
      </ModalSheetContentBlock>
    </>
  )
})

const CardPaymentMethodForm = observer(function CardPaymentMethodForm({
  vm,
}: CardPaymentMethodEditorContentProps) {
  const disabled = vm.save.pending
  const elements = useElements()
  const handleSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      void vm.save()
    },
    [elements, vm],
  )

  const handleChangeStateOrTerritory = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      vm.setStateOrTerritory(e.target.value)
    },
    [vm],
  )
  React.useEffect(() => {
    const card = elements?.getElement(CardElement)
    vm.card = card ?? null
  }, [])

  return (
    <Form onSubmit={handleSubmit}>
      <input type="submit" hidden disabled={disabled} />
      <FormRow>
        <TextInputContainer
          model={vm}
          field="fullName"
          label="Name"
          name="name"
          autoFocus
          placeholder="Please enter the full name as it is displayed on your card."
          disabled={disabled}
          error={vm.validation.getError('fullName')}
          data-test="add-card-form-full-name-input"
        />
      </FormRow>
      <FormRow>
        <TextInputContainer
          model={vm}
          field="address"
          name="address"
          label="Address"
          placeholder="Street name, number and suite/unit/apt number if applicable."
          disabled={disabled}
          error={vm.validation.getError('address')}
          data-test="add-card-form-address-input"
        />
      </FormRow>
      <FormRow>
        <TextInputContainer
          model={vm}
          field="city"
          name="city"
          label="City"
          placeholder="Enter the city name"
          disabled={disabled}
          error={vm.validation.getError('city')}
          data-test="add-card-form-city-input"
        />
      </FormRow>
      <FormRow>
        <FormColumns>
          {vm.isStateOrTerritoryRelevant && (
            <FormColumn>
              <FormGroupContainer
                model={vm}
                field="stateOrTerritory"
                label="State"
                error={vm.validation.getError('stateOrTerritory')}
              >
                <TextInput
                  placeholder="2-digit state code (e.g. FL for Florida)"
                  disabled={disabled}
                  name="state"
                  value={vm.stateOrTerritory}
                  onChange={handleChangeStateOrTerritory}
                  data-test="add-card-form-state-input"
                />
              </FormGroupContainer>
            </FormColumn>
          )}

          <FormColumn>
            <TextInputContainer
              model={vm}
              field="zipCode"
              name="zip"
              label="Zip"
              placeholder="Zip Code"
              disabled={disabled}
              error={vm.validation.getError('zipCode')}
              data-test="add-card-form-zip-input"
            />
          </FormColumn>
        </FormColumns>
      </FormRow>
      <FormRow>
        <FormGroupContainer
          model={vm}
          field="country"
          htmlFor="country"
          label="Country"
          error={vm.validation.getError('country')}
        >
          <CountryInput
            disabled={disabled}
            value={vm.country}
            onChange={vm.setCountry}
            data-test="add-card-form-country-input"
          />
        </FormGroupContainer>
      </FormRow>
      <FormRow>
        <CardInput disabled={disabled} vm={vm} />
      </FormRow>
    </Form>
  )
})
